import { get } from 'lodash';
import { removeEmpty, pullUserOutOfResp } from '../../actions/utils';
import { instance } from './api.service';

export const getUser = async (id, opts ={}) => {
  const resp = await instance.get(`/users/${id}`, { data: {} });
  return pullUserOutOfResp(resp, opts);
};

export const getUserById = async (id, opts = {}) => {
  const resp = await instance.get(`/users/id/${id}`, { data: {} });
  return pullUserOutOfResp(resp, opts);
};

export const getUsers = async (where) => {
  let resp = {};
  if (where) {
    resp = await instance.post(`/users`, {
      where,
    });
  } else {
    resp = await instance.get(`/users`, { data: {} });
  }
  return get(resp, 'data.users', []);
};

// Creates a user. `password` (optional) is the admin-entered temp password;
// when omitted the server generates one. Returns { user, tempPassword } so the
// admin can display/share the temp password.
export const createUser = async (user, password) => {
  const resp = await instance.post('/users/create', {
    user: removeEmpty(user),
    password,
  });
  return {
    user: get(resp, 'data.user.data', get(resp, 'data.user', null)),
    tempPassword: get(resp, 'data.tempPassword', null),
  };
};

// Admin/superadmin password reset (card #160). Emails the target user a
// single-use 72h reset LINK; no plaintext password is set or returned.
// Returns { success, emailSent, email }.
export const resetUserPassword = async (userId) => {
  const resp = await instance.post('/users/reset-password', { userId });
  return get(resp, 'data', {});
};

// Superadmin-only: issues an impersonation JWT for the target user.
export const impersonateUser = async (userId) => {
  const resp = await instance.post('/users/impersonate', { userId });
  return get(resp, 'data', {});
};

// MFA (TOTP) for superadmins.
export const getMfaStatus = async () => {
  const resp = await instance.get('/users/mfa/status', { data: {} });
  return get(resp, 'data', {});
};

export const enrollMfa = async () => {
  const resp = await instance.post('/users/mfa/enroll', {});
  return get(resp, 'data', {});
};

export const activateMfa = async (token) => {
  const resp = await instance.post('/users/mfa/activate', { token });
  return get(resp, 'data', {});
};

export const disableMfa = async () => {
  const resp = await instance.post('/users/mfa/disable', {});
  return get(resp, 'data', {});
};

// Card #263 round 2 — the server derives a licence REMOVAL delta (OLD − NEW) to
// fan out to sibling accounts, so it has to be able to tell "the caller emptied
// this list" apart from "the caller never mentioned this list". removeEmpty()
// drops any zero-length array, which collapses both cases to the same payload:
// an emptied licenceNumbers vanishes and reads on the server as an unmentioned
// one. Re-attach it explicitly whenever the caller passed a real array, so a
// deliberate remove-all still propagates while a partial body can never be
// mistaken for one and wipe every sibling.
// NOTE THE ORDER: removeEmpty() mutates its argument in place (`delete obj[key]`,
// app/src/actions/utils.js:219-230) and returns that same object, so the array
// has to be captured BEFORE the call — reading it afterwards yields undefined in
// precisely the remove-all case this exists to preserve.
const withExplicitLicenseNumbers = (user) => {
  const lns = get(user, 'company.licenseNumbers');
  const payload = removeEmpty(user);
  if (Array.isArray(lns) && get(payload, 'company')) payload.company.licenseNumbers = lns;
  return payload;
};

export const updateUser = async (user) => {
  const resp = await instance.post('/users/update', {
    user: withExplicitLicenseNumbers(user),
  });
  return get(resp, 'data.user.data', null);
};

export const deleteUser = async (user) => {
  const resp = await instance.post('/users/delete', {
    user: removeEmpty(user),
  }, {
    withCredentials: true,
    credentials: 'include'
  });
  return get(resp, 'data.user.data', null);
};

export const requestLicenseChange = async (userId, license, action) => {
  const resp = await instance.post('/users/license-request', {
    userId,
    license,
    action,
  });
  return get(resp, 'data', {});
};

export const approveLicenseChange = async (userId, license, action, decision) => {
  const resp = await instance.post('/users/license-approve', {
    userId,
    license,
    action,
    decision,
  });
  return get(resp, 'data', {});
};

// Returns license numbers already assigned to OTHER users (globally) relative
// to the given profile user. Used to grey out licenses in the picker.
export const getAssignedLicenses = async (userId) => {
  const resp = await instance.get(`/users/assigned-licenses/${userId}`, { data: {} });
  return get(resp, 'data.licenses', []);
};

export const getUsersWithPendingLicenses = async () => {
  const resp = await instance.get('/users/pending-licenses', { data: {} });
  return get(resp, 'data.users', []);
};

// Card #151 round 2 — Admin (role 1) + Super Admin (role 0) addresses, resolved
// from the DB rather than a static setting, for the New User Application email.
export const getAdminEmails = async () => {
  const resp = await instance.get('/users/admin-emails', { data: {} });
  return get(resp, 'data.emails', []);
};